/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/CommandPalette.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { Search, CornerDownLeft, Sparkles } from 'lucide-react';
import { I } from './ui';

export interface Command { id: string; label: string; group: string; icon: ComponentType<any>; hint?: string; keywords?: string; run: () => void }

/**
 * Cmd/Ctrl+K palette: works while a field has focus, autofocuses its input, Esc restores focus.
 * Anything that is not a command becomes a question to the agent.
 */
export function CommandPalette({ commands, initial, onClose, onAsk }: { commands: Command[]; initial: string; onClose: () => void; onAsk: (q: string) => void }) {
  const [q, setQ] = useState(initial);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLElement | null>(document.activeElement as HTMLElement | null);

  useEffect(() => {
    const f = requestAnimationFrame(() => { input.current?.focus({ preventScroll: true }); input.current?.setSelectionRange(initial.length, initial.length); });
    const prev = back.current;
    return () => { cancelAnimationFrame(f); prev?.focus?.({ preventScroll: true }); };
  }, [initial]);

  const items = useMemo(() => {
    const tokens = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const hits = tokens.length ? commands.filter((c) => { const h = `${c.label} ${c.group} ${c.keywords ?? ''}`.toLowerCase(); return tokens.every((t) => h.includes(t)); }) : commands;
    const ask: Command[] = q.trim().length > 2 ? [{ id: 'ask', label: `An Command: «${q.trim()}»`, group: 'Command', icon: Sparkles, hint: 'fragen oder beauftragen', run: () => onAsk(q.trim()) }] : [];
    return hits.length ? [...hits, ...ask] : ask;
  }, [q, commands, onAsk]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => { list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const run = (c?: Command) => { if (!c) return; onClose(); setTimeout(() => c.run(), 0); };
  const onKey = (e: React.KeyboardEvent) => {
    const n = items.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (n ? (a + 1) % n : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (n ? (a - 1 + n) % n : 0)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setActive((a) => Math.min(n - 1, a + 8)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 8)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(items[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (e.key === 'Tab') { e.preventDefault(); }
  };

  let lastGroup = '';
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal palette" role="dialog" aria-modal="true" aria-label="Befehlspalette">
        <div className="combo-search">
          <I icon={Search} />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Suchen, springen oder Kontor fragen…" role="combobox" aria-expanded="true" aria-controls="palette-list" />
        </div>
        <div className="combo-list" role="listbox" id="palette-list" ref={list}>
          {!items.length && <div className="combo-empty">Tippe eine Frage oder einen Befehl</div>}
          {items.map((c, i) => {
            const head = c.group !== lastGroup ? <div className="group-label" key={`g-${c.group}-${i}`}>{c.group}</div> : null;
            lastGroup = c.group;
            return [head, (
              <div key={c.id} role="option" aria-selected={i === active} className="combo-opt" onMouseMove={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); run(c); }}>
                <I icon={c.icon} sm />
                <span className="primary">{c.label}</span>
                {c.hint && <span className="secondary">{c.hint}</span>}
              </div>
            )];
          })}
        </div>
        <div className="palette-foot"><span><kbd>↑</kbd> <kbd>↓</kbd> wählen</span><span><I icon={CornerDownLeft} sm /> ausführen</span><span><kbd>esc</kbd> schliessen</span></div>
      </div>
    </>,
    document.body,
  );
}
